import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from '@phosphor-icons/react';

// Component to handle map center updates
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 14);
    }
  }, [center, map]);
  return null;
}

export default function LocationMap({ city, zipCode, isBooked = false }) {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const geocodeLocation = async () => {
      try {
        setLoading(true);
        // Use OpenStreetMap Nominatim for geocoding (free, no API key needed)
        const query = zipCode ? `${zipCode}, USA` : `${city}, USA`;
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
          {
            headers: {
              'User-Agent': '100Nook/1.0'
            }
          }
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          // Add slight randomization for privacy (within ~0.5 mile radius)
          const randomOffset = () => (Math.random() - 0.5) * 0.01;
          setCoordinates({
            lat: parseFloat(data[0].lat) + randomOffset(),
            lng: parseFloat(data[0].lon) + randomOffset()
          });
        } else {
          setError('Location not found');
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setError('Unable to load map');
      } finally {
        setLoading(false);
      }
    };

    if (city || zipCode) {
      geocodeLocation();
    }
  }, [city, zipCode]);

  if (loading) {
    return (
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <MapPin size={20} weight="duotone" className="text-[#D4AF37]" />
          Location
        </h3>
        <div className="h-48 bg-[#0A0A0A] rounded flex items-center justify-center">
          <span className="text-zinc-500">Loading map...</span>
        </div>
      </div>
    );
  }

  if (error || !coordinates) {
    return (
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <MapPin size={20} weight="duotone" className="text-[#D4AF37]" />
          Location
        </h3>
        <div className="h-48 bg-[#0A0A0A] rounded flex items-center justify-center flex-col gap-2">
          <MapPin size={32} className="text-zinc-600" />
          <span className="text-zinc-500 text-sm">{city}{zipCode ? `, ${zipCode}` : ''}</span>
          <span className="text-zinc-600 text-xs">Exact location provided after booking</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <MapPin size={20} weight="duotone" className="text-[#D4AF37]" />
        {isBooked ? 'Exact Location' : 'Approximate Location'}
      </h3>
      
      <div className="h-48 md:h-64 rounded overflow-hidden relative">
        <MapContainer
          center={[coordinates.lat, coordinates.lng]}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <MapUpdater center={[coordinates.lat, coordinates.lng]} />
          
          {/* Show approximate area circle when not booked */}
          {!isBooked && (
            <Circle
              center={[coordinates.lat, coordinates.lng]}
              radius={800}
              pathOptions={{
                color: '#D4AF37',
                fillColor: '#D4AF37',
                fillOpacity: 0.15,
                weight: 2
              }}
            />
          )}
          
          {/* Show exact marker when booked */}
          {isBooked && (
            <Circle
              center={[coordinates.lat, coordinates.lng]}
              radius={50}
              pathOptions={{
                color: '#D4AF37',
                fillColor: '#D4AF37',
                fillOpacity: 0.8,
                weight: 2
              }}
            />
          )}
        </MapContainer>
      </div>
      
      <div className="mt-3 flex items-center gap-2 text-sm">
        <div className="w-3 h-3 rounded-full bg-[#D4AF37]/30 border border-[#D4AF37]"></div>
        <span className="text-zinc-400">
          {isBooked 
            ? 'Exact location shown' 
            : 'Approximate area - exact address provided after booking confirmation'}
        </span>
      </div>
    </div>
  );
}
